import React from 'react';
import {
  Edit2, ExternalLink, Twitter, Linkedin, Github, Youtube, MessageSquare as DiscordIcon,
  HelpCircle, Briefcase, Mail, EyeOff, Share2
} from 'lucide-react';
import { AgentData_V10, AgentHubPageViewMode, AgentSocialMediaLinkV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { IconButton } from '@dopomogai/ui';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { INNER_CARD_ANIMATE_RESTING, INNER_CARD_HOVER, INNER_CARD_TRANSITION_HOVER } from '../../shared/animationConstants';

interface SocialContactSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

// Mapping social platform names to Lucide icons
const socialIconMap: Record<string, React.ReactNode> = {
  Twitter: <Twitter size={20} />,
  LinkedIn: <Linkedin size={20} />,
  Discord: <DiscordIcon size={20} />,
  GitHub: <Github size={20} />,
  YouTube: <Youtube size={20} />,
};

interface ContactCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    buttonText: string;
    actionType: string;
    actionPayload?: any;
    onAction: (actionType: string, payload?: any) => void;
}

const ContactCard: React.FC<ContactCardProps> = ({ icon, title, description, buttonText, actionType, actionPayload, onAction }) => (
    <motion.div
        animate={INNER_CARD_ANIMATE_RESTING}
        whileHover={INNER_CARD_HOVER}
        transition={INNER_CARD_TRANSITION_HOVER}
        className="bg-white p-5 rounded-xl border-2 border-black flex flex-col items-start h-full"
    >
        <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-slate-100 rounded-lg border-2 border-black shadow-sm">
                {icon}
            </div>
            <h4 className="text-md font-semibold text-gray-800">{title}</h4>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed flex-grow mb-4">{description}</p>
        <Button
            text={buttonText}
            variant="secondary"
            size="sm"
            onClick={() => onAction(actionType, actionPayload)}
            className="w-full mt-auto"
        />
    </motion.div>
);


export const SocialContactSection = React.forwardRef<HTMLDivElement, SocialContactSectionProps>(
  ({ agentData, viewMode, onAction, headerBgColor, id }, ref) => {
  const { t } = useTranslation('agent-common');
  const isOwnerView = viewMode === 'owner_dashboard';

  const displaySocialLinks = agentData.agentSocialMediaLinks
    ? isOwnerView
      ? agentData.agentSocialMediaLinks
      : agentData.agentSocialMediaLinks.filter(link => link.isPubliclyVisible !== false)
    : [];

  const hasContent = displaySocialLinks.length > 0 || agentData.contactForSupportUrlPublic;

  if (!hasContent && !isOwnerView) {
    return null;
  }

  return (
    <Card
      id={id}
      ref={ref}
      title={t('socialContactSection.defaultTitle', "Connect & Get Support")}
      headerBgColor={headerBgColor}
      icon={<Share2 size={22} className="text-slate-600" />}
      actionButton={
        isOwnerView && agentData.manageSocialMediaLinksUrl && (
          <Button
            text="Manage Links"
            variant="outline"
            size="sm"
            icon={<Edit2 size={14} />}
            onClick={() => onAction('MANAGE_SOCIAL_MEDIA', { url: agentData.manageSocialMediaLinksUrl })}
          />
        )
      }
    >
      {!hasContent && isOwnerView ? (
        <div className="text-center py-10">
          <Share2 size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 mb-4">{t('socialContactSection.noSocialConfigured', 'No social or support links are configured.')}</p>
          {agentData.manageSocialMediaLinksUrl && (
            <Button
              text="Configure Links"
              variant="primary"
              onClick={() => onAction('MANAGE_SOCIAL_MEDIA', { url: agentData.manageSocialMediaLinksUrl })}
            />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Column 1: Social Media Links */}
          {displaySocialLinks.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">{t('socialContactSection.followTitle', 'Follow Our Journey')}</h3>
              <div className="flex flex-wrap gap-3">
                {displaySocialLinks.map((link) => (
                  <div key={link.id} className="relative">
                     <IconButton
                        icon={socialIconMap[link.platformName] || <ExternalLink size={20} />}
                        variant="secondary"
                        size="md"
                        onClick={() => onAction('SOCIAL_LINK_CLICKED', { platform: link.platformName, url: link.url })}
                        tooltip={link.handle ? `${link.platformName}: ${link.handle}` : link.platformName}
                        ariaLabel={`${agentData.name} on ${link.platformName}`}
                        applyNeobrutalistEffects={true}
                      />
                      {isOwnerView && link.isPubliclyVisible === false && (
                          <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-600 text-white rounded-full flex items-center justify-center border-2 border-white" title="Hidden from public">
                              <EyeOff size={12}/>
                          </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Column 2: Contact Options */}
          {agentData.contactForSupportUrlPublic && (
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">{t('socialContactSection.assistanceTitle', 'Need Assistance?')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <ContactCard
                        icon={<HelpCircle size={22} className="text-blue-600"/>}
                        title={t('socialContactSection.agentSupportTitle', 'Agent Support')}
                        description={t('socialContactSection.agentSupportDesc', 'Questions about using or integrating this specific agent.')}
                        buttonText={t('socialContactSection.getSupportButton', 'Get Support')}
                        actionType="NAVIGATE_TO_CONTACT"
                        actionPayload={{ url: agentData.contactForSupportUrlPublic, inquiry_type: 'agent_support', agent_name: agentData.name }}
                        onAction={onAction}
                    />
                     <ContactCard
                        icon={<Briefcase size={22} className="text-emerald-600"/>}
                        title={t('socialContactSection.customSolutionTitle', 'Custom Solutions')}
                        description={t('socialContactSection.customSolutionDesc', 'Discuss a partnership or a custom AI project for your business.')}
                        buttonText={t('socialContactSection.startConversationButton', 'Start a Conversation')}
                        actionType="NAVIGATE_TO_CONTACT"
                        actionPayload={{ url: agentData.contactForSupportUrlPublic, inquiry_type: 'custom_solution' }}
                        onAction={onAction}
                    />
                </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
});

SocialContactSection.displayName = "SocialContactSection";
